import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../home/components/Footer';

const tableOfContents = [
  { id: 'what-is', label: 'What Is an AI Receptionist?' },
  { id: 'technology', label: 'The Technology Behind It' },
  { id: 'call-flow', label: 'How a Call Actually Works' },
  { id: 'nlp', label: 'Natural Language Processing' },
  { id: 'scheduling', label: 'Appointment Scheduling' },
  { id: 'learning', label: 'Continuous Learning' },
  { id: 'benefits', label: 'Key Benefits' },
  { id: 'getting-started', label: 'Getting Started' },
];

const relatedPosts = [
  {
    title: '5 Signs Your Business Needs an AI Receptionist',
    category: 'Business Tips',
    readTime: '5 min read',
    slug: '/blog',
    image:
      'https://public.readdy.ai/ai/img_res/bc1e36aa083ea3b7d5263dfff9aba2ac.jpg',
  },
  {
    title: 'The Complete Guide to AI Appointment Scheduling',
    category: 'Product Guide',
    readTime: '6 min read',
    slug: '/blog',
    image:
      'https://readdy.ai/api/search-image?query=Digital%20calendar%20interface%20with%20smart%20scheduling%20features%2C%20clean%20modern%20UI%20design%2C%20appointment%20slots%20highlighted%2C%20professional%20business%20tool%20visualization%20%2C%20soft%20warm%20tones&width=600&height=400&seq=blog-post-002&orientation=landscape',
  },
  {
    title: 'AI vs. Traditional Answering Services: A Cost Comparison',
    category: 'Business Tips',
    readTime: '5 min read',
    slug: '/blog',
    image:
      'https://readdy.ai/api/search-image?query=Split%20comparison%20infographic%20showing%20traditional%20phone%20operator%20versus%20modern%20AI%20technology%2C%20clean%20design%20%2C%20professional%20business%20concept%20warm%20neutral%20tones%20with%20accent%20colors&width=600&height=400&seq=blog-post-005&orientation=landscape',
  },
];

export default function HowAIReceptionistsWorkPage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('what-is');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<
    'idle' | 'sending' | 'success' | 'error'
  >('idle');

  // -------------------------------------------------------------------------
  // Scroll handling – adds a shadow to the navbar after the user scrolls
  // -------------------------------------------------------------------------
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ensure page starts at the top when navigated to
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // -------------------------------------------------------------------------
  // IntersectionObserver – updates active TOC entry based on scroll position
  // -------------------------------------------------------------------------
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -60% 0px' }
    );

    tableOfContents.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // -------------------------------------------------------------------------
  // Newsletter form submission – with robust error handling
  // -------------------------------------------------------------------------
  const handleNewsletterSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    if (!newsletterEmail) return;

    setNewsletterStatus('sending');

    try {
      const res = await fetch(
        'https://readdy.ai/api/form/d6b23stbiueuoqm1ssbg',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ email: newsletterEmail }).toString(),
        }
      );

      if (res.ok) {
        setNewsletterStatus('success');
        setNewsletterEmail('');
      } else {
        setNewsletterStatus('error');
      }
    } catch (err) {
      console.error('Newsletter subscription error:', err);
      setNewsletterStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/e31f2f4702878072217cd119276d8644/5b56961b60c36d4ff2ff86c82d13a1e4.png"
              alt="AI Receptionist Logo"
              className="h-12 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              About
            </Link>
            <Link
              to="/blog"
              className="text-sm font-medium text-teal-600 transition-colors cursor-pointer"
            >
              Blog
            </Link>
            <Link
              to="/demo"
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              Demo
            </Link>
            <Link
              to="/contact"
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              Contact
            </Link>
            <a
              href="/#contact"
              className="px-6 py-2 bg-gradient-to-r from-teal-600 to-emerald-600 text-white text-sm font-medium rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-20">
        <div className="w-full h-[420px] relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20futuristic%20communication%20network%20visualization%20with%20flowing%20data%20streams%20and%20voice%20waveforms%2C%20warm%20teal%20and%20coral%20color%20palette%2C%20modern%20digital%20art%20style%2C%20clean%20composition%20with%20soft%20gradients%2C%20technology%20concept%20art%20for%20AI%20voice%20assistant&width=1920&height=840&seq=blog-ai-hero-001&orientation=landscape"
            alt="How AI Receptionists Work"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-teal-500 text-white text-xs font-bold rounded-full">
                  PRODUCT GUIDE
                </span>
                <span className="text-xs text-gray-200">Feb 20, 2025</span>
                <span className="text-xs text-gray-200">&bull; 10 min read</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-3">
                How AI Receptionists Work: A Complete Guide
              </h1>
              <p className="text-lg text-gray-200 max-w-2xl">
                From voice recognition to appointment booking &mdash; discover
                the technology that powers your 24/7 virtual front desk.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Author Bar */}
      <section className="border-b border-gray-200 px-6">
        <div className="max-w-4xl mx-auto py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center text-white font-bold text-sm">
              AS
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Atlas System Team
              </p>
              <p className="text-xs text-gray-500">
                AI &amp; Business Communications
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() =>
                navigator.clipboard?.writeText(window.location.href)
              }
              className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
              aria-label="Copy link"
            >
              <i className="ri-link text-gray-600 text-base"></i>
            </button>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-[#1877F2] hover:text-white transition-colors cursor-pointer group"
              aria-label="Share on Facebook"
            >
              <i className="ri-facebook-fill text-gray-600 group-hover:text-white text-base"></i>
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-[#0A66C2] hover:text-white transition-colors cursor-pointer group"
              aria-label="Share on LinkedIn"
            >
              <i className="ri-linkedin-fill text-gray-600 group-hover:text-white text-base"></i>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent('How AI Receptionists Work: A Complete Guide')}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-black hover:text-white transition-colors cursor-pointer group"
              aria-label="Share on X"
            >
              <i className="ri-twitter-x-fill text-gray-600 group-hover:text-white text-base"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Content + Sidebar */}
      <section className="px-6 py-12">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-12">
          {/* Main Content */}
          <article className="flex-1 max-w-3xl">
            {/* Intro */}
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              If you&apos;ve ever wondered how an AI receptionist can answer
              phone calls, understand what callers need, and book appointments
              &mdash; all without a human lifting a finger &mdash; you&apos;re
              in the right place. In this guide, we break down every layer of
              the technology so you can see exactly what happens from the
              moment a customer dials your number.
            </p>

            {/* Section 1 */}
            <h2
              id="what-is"
              className="text-2xl font-bold text-gray-900 mt-12 mb-4 scroll-mt-28"
            >
              <a href="#what-is" className="hover:text-teal-600 transition-colors">
                What Is an AI Receptionist?
              </a>
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              An <strong>AI receptionist</strong> is a virtual assistant that
              answers incoming phone calls on behalf of your business. Unlike a
              traditional voicemail or phone tree, it holds natural, human-like
              conversations with callers. It can greet customers, answer
              frequently asked questions, capture lead information, route urgent
              calls, and schedule appointments &mdash; all in real time.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Think of it as a highly trained front-desk employee who never
              takes a break, never calls in sick, and works around the clock
              &mdash; including weekends and holidays. For contractors,
              medical practices, salons, and other local service businesses,
              this means <strong>zero missed calls</strong> and a dramatically
              better customer experience.
            </p>
            <div className="my-8 rounded-xl overflow-hidden w-full h-72">
              <img
                src="https://readdy.ai/api/search-image?query=Friendly%20AI%20assistant%20hologram%20at%20a%20modern%20reception%20desk%20greeting%20a%20customer%2C%20warm%20teal%20ambient%20lighting%2C%20clean%20futuristic%20interior%20design%2C%20professional%20business%20environment%20%2C%20welcoming%20atmosphere%2C%20soft%20focus%20background&width=900&height=450&seq=blog-ai-section1&orientation=landscape"
                alt="AI receptionist greeting a customer"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Section 2 */}
            <h2
              id="technology"
              className="text-2xl font-bold text-gray-900 mt-12 mb-4 scroll-mt-28"
            >
              <a href="#technology" className="hover:text-teal-600 transition-colors">
                The Technology Behind It
              </a>
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              AI receptionists combine several cutting‑edge technologies into a
              seamless experience. Here are the core building blocks:
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              {[
                {
                  icon: 'ri-mic-line',
                  title: 'Speech Recognition (ASR)',
                  desc: 'Converts spoken words into text in real time, supporting 30+ languages and accents.',
                },
                {
                  icon: 'ri-brain-line',
                  title: 'Natural Language Processing',
                  desc: 'Understands the meaning and intent behind what callers say, not just the words.',
                },
                {
                  icon: 'ri-chat-voice-line',
                  title: 'Text-to-Speech (TTS)',
                  desc: 'Generates natural-sounding voice responses that feel conversational, not robotic.',
                },
                {
                  icon: 'ri-database-2-line',
                  title: 'Knowledge Base',
                  desc: 'Stores your business info, FAQs, services, and pricing so the AI can answer accurately.',
                },
              ].map(item => (
                <div
                  key={item.title}
                  className="bg-gray-50 rounded-xl p-5 border border-gray-100"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-100 mb-3">
                    <i className={`${item.icon} text-teal-600 text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* Section 3 */}
            <h2
              id="call-flow"
              className="text-2xl font-bold text-gray-900 mt-12 mb-4 scroll-mt-28"
            >
              <a href="#call-flow" className="hover:text-teal-600 transition-colors">
                How a Call Actually Works
              </a>
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              Let&apos;s walk through a typical call step by step so you can
              see the AI receptionist in action:
            </p>
            <div className="space-y-6 mb-8">
              {[
                {
                  step: '1',
                  title: 'Customer Calls Your Number',
                  desc:
                    "The call is routed through your existing phone system to the AI receptionist. There's no new hardware needed &mdash; it works with your current business number.",
                },
                {
                  step: '2',
                  title: 'AI Greets the Caller',
                  desc:
                    'Within milliseconds, the AI answers with a warm, professional greeting customized to your brand. For example: "Hi, thanks for calling Bright Smile Dental! How can I help you today?"',
                },
                {
                  step: '3',
                  title: 'Caller States Their Need',
                  desc:
                    'The caller speaks naturally. They might say "I need to book a cleaning" or "What are your hours?" The speech recognition engine transcribes this in real time.',
                },
                {
                  step: '4',
                  title: 'AI Understands Intent',
                  desc:
                    "Natural language processing analyzes the transcription to determine the caller's intent &mdash; whether it's booking an appointment, asking a question, or requesting a callback.",
                },
                {
                  step: '5',
                  title: 'AI Takes Action',
                  desc:
                    'Based on the intent, the AI either answers the question from your knowledge base, books an appointment on your calendar, captures lead details, or transfers the call to a team member.',
                },
                {
                  step: '6',
                  title: 'Follow-Up & Summary',
                  desc:
                    "After the call, you receive a detailed summary via text or email including the caller's name, reason for calling, and any actions taken.",
                },
              ].map(item => (
                <div key={item.step} className="flex gap-4">
                  <div className="w-10 h-10 flex-shrink-0 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center text-white font-bold text-sm">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Section 4 */}
            <h2
              id="nlp"
              className="text-2xl font-bold text-gray-900 mt-12 mb-4 scroll-mt-28"
            >
              <a href="#nlp" className="hover:text-teal-600 transition-colors">
                Natural Language Processing: The Brain of the Operation
              </a>
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              The real magic happens in the NLP layer. Unlike old‑school phone
              trees that force callers to press buttons, modern AI receptionists
              understand <strong>free-form speech</strong>. A caller can say:
            </p>
            <blockquote className="border-l-4 border-teal-500 pl-6 py-2 my-6 bg-teal-50/50 rounded-r-lg">
              <p className="text-base italic text-gray-700">
                &ldquo;Hey, I was wondering if you guys have any openings this
                Thursday afternoon for a haircut and maybe a beard trim too?&rdquo;
              </p>
            </blockquote>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              The AI breaks this down into structured data:{' '}
              <strong>Service</strong> = haircut + beard trim,{' '}
              <strong>Preferred date</strong> = Thursday,{' '}
              <strong>Preferred time</strong> = afternoon. It then checks your
              calendar for availability and responds conversationally.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              This works because the AI is trained on millions of real
              conversations and fine‑tuned specifically for your industry. It
              understands slang, accents, background noise, and even callers who
              change their mind mid‑sentence.
            </p>

            {/* Section 5 */}
            <h2
              id="scheduling"
              className="text-2xl font-bold text-gray-900 mt-12 mb-4 scroll-mt-28"
            >
              <a href="#scheduling" className="hover:text-teal-600 transition-colors">
                Appointment Scheduling: Seamless Calendar Integration
              </a>
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              One of the most valuable features is{' '}
              <strong>real-time appointment booking</strong>. The AI receptionist
              connects directly to your calendar (Google Calendar, Outlook, or
              your industry‑specific software) and can:
            </p>
            <ul className="space-y-3 mb-6">
              {[
                'Check real-time availability across multiple team members',
                'Book, reschedule, or cancel appointments during the call',
                'Send instant confirmation texts and emails to the caller',
                'Add buffer time between appointments automatically',
                'Respect your business hours and blocked‑off time slots',
                'Handle recurring appointments and waitlists',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-teal-600 text-lg"></i>
                  </div>
                  <span className="text-sm text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <div className="my-8 rounded-xl overflow-hidden w-full h-64">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20digital%20calendar%20interface%20showing%20appointment%20scheduling%20with%20AI%20assistant%2C%20clean%20minimal%20design%20%2C%20teal%20and%20white%20color%20scheme%2C%20time%20slots%20and%20booking%20confirmations%20visible%2C%20professional%20business%20software%20mockup&width=900&height=400&seq=blog-ai-scheduling&orientation=landscape"
                alt="AI appointment scheduling interface"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Section 6 */}
            <h2
              id="learning"
              className="text-2xl font-bold text-gray-900 mt-12 mb-4 scroll-mt-28"
            >
              <a href="#learning" className="hover:text-teal-600 transition-colors">
                Continuous Learning &amp; Improvement
              </a>
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              AI receptionists get smarter over time. Every call provides data
              that helps the system improve. Here&apos;s how the learning loop
              works:
            </p>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 mb-6">
              <div className="grid sm:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-100 mx-auto mb-3">
                    <i className="ri-line-chart-line text-teal-600 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">Analyze</h3>
                  <p className="text-xs text-gray-600">
                    Every call is analyzed for accuracy, tone, and outcome.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-100 mx-auto mb-3">
                    <i className="ri-settings-3-line text-teal-600 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">Optimize</h3>
                  <p className="text-xs text-gray-600">
                    Responses are refined based on caller satisfaction and business
                    goals.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-100 mx-auto mb-3">
                    <i className="ri-rocket-line text-teal-600 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">Deploy</h3>
                  <p className="text-xs text-gray-600">
                    Improvements go live automatically &mdash; no manual updates
                    needed.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 7 */}
            <h2
              id="benefits"
              className="text-2xl font-bold text-gray-900 mt-12 mb-4 scroll-mt-28"
            >
              <a href="#benefits" className="hover:text-teal-600 transition-colors">
                Key Benefits for Your Business
              </a>
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                {
                  icon: 'ri-time-line',
                  title: '24/7 Availability',
                  desc: 'Never miss a call, even at 2 AM on a holiday.',
                },
                {
                  icon: 'ri-money-dollar-circle-line',
                  title: 'Cost Savings',
                  desc: 'Up to 80% cheaper than a full-time receptionist.',
                },
                {
                  icon: 'ri-global-line',
                  title: '30+ Languages',
                  desc: 'Serve diverse customers in their preferred language.',
                },
                {
                  icon: 'ri-speed-line',
                  title: 'Instant Response',
                  desc: 'Answers in under 1 second &mdash; no hold music.',
                },
                {
                  icon: 'ri-bar-chart-box-line',
                  title: 'Call Analytics',
                  desc: 'Detailed insights into call volume, peak times, and outcomes.',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Data Security',
                  desc: 'Enterprise‑grade encryption protects every conversation.',
                },
              ].map(item => (
                <div
                  key={item.title}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-100 flex-shrink-0">
                    <i className={`${item.icon} text-emerald-600 text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-0.5">
                      {item.title}
                    </h3>
                    <p className="text-xs text-gray-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Section 8 */}
            <h2
              id="getting-started"
              className="text-2xl font-bold text-gray-900 mt-12 mb-4 scroll-mt-28"
            >
              <a href="#getting-started" className="hover:text-teal-600 transition-colors">
                Getting Started Is Easier Than You Think
              </a>
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Setting up an AI receptionist for your business takes less than 10
              minutes. There&apos;s no hardware to install, no complex
              integrations, and no technical skills required. Simply:
            </p>
            <ol className="space-y-3 mb-8">
              {[
                'Sign up and tell us about your business',
                'Customize your greeting, FAQs, and scheduling preferences',
                'Forward your business number (or get a new one)',
                'Start receiving AI‑answered calls immediately',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 flex-shrink-0 rounded-full bg-teal-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-sm text-gray-700">{item}</span>
                </li>
              ))}
            </ol>

            {/* CTA */}
            <div className="bg-gradient-to-br from-teal-600 to-emerald-600 rounded-2xl p-8 text-center mb-12">
              <h3 className="text-2xl font-bold text-white mb-3">
                Ready to See It in Action?
              </h3>
              <p className="text-teal-100 mb-6 text-sm max-w-md mx-auto">
                Try our AI receptionist free for 7 days. No credit card required.
                Set up in minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="/#contact"
                  className="px-6 py-3 bg-white text-teal-700 font-semibold rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-sm"
                >
                  Start Free Trial
                </a>
                <Link
                  to="/demo"
                  className="px-6 py-3 border-2 border-white text-white font-semibold rounded-full hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer text-sm"
                >
                  Watch Demo
                </Link>
              </div>
            </div>

            {/* Share This Article */}
            <div className="border-t border-gray-200 pt-8 mb-12">
              <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
                Share This Article
              </h3>
              <div className="flex items-center justify-center gap-3">
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gray-100 hover:bg-[#1877F2] hover:text-white transition-all cursor-pointer group"
                  aria-label="Share on Facebook"
                >
                  <i className="ri-facebook-fill text-gray-600 group-hover:text-white text-base"></i>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-white whitespace-nowrap">Facebook</span>
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gray-100 hover:bg-[#0A66C2] hover:text-white transition-all cursor-pointer group"
                  aria-label="Share on LinkedIn"
                >
                  <i className="ri-linkedin-fill text-gray-600 group-hover:text-white text-base"></i>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-white whitespace-nowrap">LinkedIn</span>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent('How AI Receptionists Work: A Complete Guide')}`}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gray-100 hover:bg-black hover:text-white transition-all cursor-pointer group"
                  aria-label="Share on X"
                >
                  <i className="ri-twitter-x-fill text-gray-600 group-hover:text-white text-base"></i>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-white whitespace-nowrap">X</span>
                </a>
                <button
                  onClick={() => navigator.clipboard?.writeText(window.location.href)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gray-100 hover:bg-gray-800 hover:text-white transition-all cursor-pointer group"
                  aria-label="Copy link"
                >
                  <i className="ri-link text-gray-600 group-hover:text-white text-base"></i>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-white whitespace-nowrap">Copy Link</span>
                </button>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="w-full lg:w-80 flex-shrink-0">
            <div className="lg:sticky lg:top-28 space-y-8">
              {/* Table of Contents */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <p className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-4">
                  In This Article
                </p>
                <nav className="space-y-1">
                  {tableOfContents.map(item => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className={`block text-sm py-1.5 pl-3 border-l-2 transition-all cursor-pointer ${
                        activeSection === item.id
                          ? 'border-teal-600 text-teal-700 font-semibold'
                          : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
                      }`}
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>

              {/* Newsletter */}
              <div className="bg-gradient-to-br from-teal-600 to-emerald-600 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-2">
                  Stay Updated
                </h3>
                <p className="text-sm text-teal-100 mb-4">
                  Get AI tips and product updates in your inbox.
                </p>
                {newsletterStatus === 'success' ? (
                  <div className="bg-white/20 rounded-lg p-4 text-center">
                    <i className="ri-check-double-line text-white text-2xl mb-1"></i>
                    <p className="text-sm text-white font-medium">
                      You&apos;re subscribed!
                    </p>
                  </div>
                ) : (
                  <form
                    id="blog-newsletter-sidebar"
                    data-readdy-form
                    onSubmit={handleNewsletterSubmit}
                    className="space-y-3"
                  >
                    <input
                      type="email"
                      name="email"
                      value={newsletterEmail}
                      onChange={e => setNewsletterEmail(e.target.value)}
                      placeholder="Your email address"
                      required
                      className="w-full px-4 py-2.5 rounded-lg text-sm bg-white/90 text-gray-800 placeholder-gray-500 focus:ring-2 focus:ring-white outline-none"
                    />
                    <button
                      type="submit"
                      disabled={newsletterStatus === 'sending'}
                      className="w-full py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {newsletterStatus === 'sending'
                        ? 'Subscribing...'
                        : 'Subscribe'}
                    </button>
                    {newsletterStatus === 'error' && (
                      <p className="text-xs text-red-200">
                        Something went wrong. Please try again.
                      </p>
                    )}
                  </form>
                )}
              </div>

              {/* Related Posts */}
              <div>
                <p className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-4">
                  Related Articles
                </p>
                <div className="space-y-4">
                  {relatedPosts.map(post => (
                    <Link
                      key={post.title}
                      to={post.slug}
                      className="flex gap-3 group cursor-pointer"
                    >
                      <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Use a <span> instead of an <a> inside <Link> to avoid nesting errors */}
                        <h4 className="text-sm font-semibold text-gray-900 group-hover:text-teal-600 transition-colors leading-snug line-clamp-2">
                          <span>{post.title}</span>
                        </h4>
                        <p className="text-xs text-gray-400 mt-1">
                          {post.readTime}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
}
